import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import AdminLoader from "../../components/common/loader/AdminLoader";
import { useDispatch, useSelector } from "react-redux";
import {
  incrementLoginAttempts,
  resetLoginAttempts,
  tryLogin,
} from "../../store/slices/sessionSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import logo from "../../assets/peopleskillLogo.svg"

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const loginAttempts = useSelector((state) => state.session.loginAttempts);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false); // State for toggling password visibility

  const togglePassword = () => {
    setPasswordVisible(!passwordVisible); // Toggle the password visibility
  };

  const handleMouseEnter = () => {
    setPasswordVisible(true);
  };

  const handleMouseLeave = () => {
    setPasswordVisible(false);
  };
  const loginFailureCallback = useCallback(() => {
    dispatch(incrementLoginAttempts()); // ✅ Update Redux count
    setIsLoading(false);
  }, [dispatch]);

  const loginSuccessCallback = useCallback(
    async (responseauth) => {
      setIsLoading(false);
      navigate(`/dashboard/`);
      dispatch(resetLoginAttempts());
      enqueueSnackbar("Logged in successfully!", {
        variant: "success",
      });
    },
    [navigate, enqueueSnackbar, setIsLoading, dispatch]
  );

  const handleLogin = useCallback(async () => {
    if (!email) {
      enqueueSnackbar("Please Enter Email", { variant: "error" });
    } else if (!password) {
      enqueueSnackbar("Please Enter Password", { variant: "error" });
    } else {
      try {
        setIsLoading(true);
        const resultAction = await dispatch(
          tryLogin({
            email,
            password,
            loginSuccessCallback,
            loginFailureCallback,
          })
        );
        unwrapResult(resultAction); // ✅ Optional for direct error throw
      } catch (error) {
      }
    }
  }, [
    email,
    password,
    enqueueSnackbar,
    dispatch,
    loginSuccessCallback,
    loginFailureCallback,
  ]);

  useEffect(() => {
    if (loginAttempts >= 3) {
      enqueueSnackbar(
        "Too many failed attempts. Redirecting to Forgot Password...",
        { variant: "error" }
      );
      setTimeout(() => {
        dispatch(resetLoginAttempts());
        navigate("/forgotpw");
      }, 3000);
    } else if (loginAttempts > 0) {
      enqueueSnackbar("Incorrect Login Credentials!", {
        variant: "error",
      });
      setIsLoading(false);
    }
  }, [enqueueSnackbar, loginAttempts, navigate, dispatch]);

  useEffect(() => { }, [navigate, location.pathname]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter" && !isLoading) {
        handleLogin();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [email, password, handleLogin, isLoading]);


  const forgotpw = () => {
    navigate("/forgotpw", {
      state: { email: email },  // `email` is your state variable
    });
  };


  return (
    <div className="min-h-screen flex items-center bg-gray-100 justify-center px-4">
      {isLoading ? (
        <AdminLoader isLoading={isLoading} />
      ) : (
        <div className="w-full max-w-md md:max-w-2xl lg:px-48 text-center my-12 sm:my-20 px-6 sm:px-10 py-8 sm:py-12 bg-[#fbfafb] overflow-hidden shadow-2xl rounded-lg border border-gray-200">
          <h1 className="text-3xl sm:text-5xl font-extrabold">
            <img
              src={logo}
              alt="loginlogo"
              className="scale-90 mx-auto"
            />
          </h1>
          <br />
          <h1 className="font-extrabold text-xl sm:text-2xl">Login</h1>
          <br />

          <div className="flex items-center">
            <FaUser size={20} className="text-gray-600" />
            <input
              className="border ml-2 w-full border-gray-300 rounded-md p-2 shadow-sm outline-none"
              value={email}
              type="text"
              placeholder="Enter Email"
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
            />
          </div>

          <br />

          <div className="flex items-center relative">
            <FaLock size={20} className="text-gray-600" />
            <input
              className="border ml-2 w-full border-gray-300 rounded-md p-2 shadow-sm outline-none"
              value={password}
              type={passwordVisible ? "text" : "password"}
              placeholder="Enter Password"
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="off"
            />
            <div
              className="absolute right-4 cursor-pointer"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onClick={togglePassword}
              style={{ top: "50%", transform: "translateY(-50%)" }}
            >
              {passwordVisible ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
            </div>
          </div>

          <h1
            onClick={forgotpw}
            className="text-right mt-2 text-sm underline text-blue-900 hover:text-red-600 cursor-pointer"
          >
            Forgot Password?
          </h1>
          <br />

          <button
            onClick={handleLogin}
            disabled={isLoading}
            className={`py-1 text-center mt-2 overflow-hidden shadow-md rounded-sm text-white px-6 
            file: ${isLoading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-900"
              }`}
          >
            {isLoading ? "Logging in..." : "Login"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Login;
